import { UserField } from '@dadohr/shared/domainTypes/experience';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { Button, Card, TextField, Typography } from '@material-ui/core';
import { ChevronLeft, Eye } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router';
import { Loader } from '../../../../../components/Loader';
import { NoAccess } from '../../../../../components/NoAccess';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { PageBody } from '../../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../../layout/PageToolbar';
import { useRouter } from '../../../../../hooks/useRouter';
import { useRoutes } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { hasPermissionToAccessPage } from '../../../../../services/permissions';
import { useUserRuleConfigWithAutocompletion } from '../../../../../services/user';
import {
  createTargetAudience,
  updateTargetAudience,
  useTargetAudienceById
} from '../../../../../services/targetAudience';
import {
  EMPTY_RULE_GROUP,
  hasIncompleteCondition,
  normalizeBlankOperatorValues,
  RuleBuilder
} from '../../../../Experiences/components/RuleBuilder';
import { AudienceMatchDialog } from '../../../../Experiences/components/Builders/TaskBuilder/components/Audiences';
import { useUsersSideNav } from '../../sideNav';

// Prefill for a brand-new audience handed off from another editor (e.g. the
// Task Builder's "Add to segment library" button). Carried in a URL query
// param rather than router state, since that button opens this page in a
// new tab — a separate JS runtime with no access to the opener's history
// state — so the payload has to travel in the URL itself.
const parsePrefill = (
  search: string
): { name: string; rule: Rule<UserField> } | undefined => {
  const raw = new URLSearchParams(search).get('prefill');
  if (!raw) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const TargetAudienceBuilderContent = ({
  audienceId
}: {
  audienceId: string | null;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { ROUTES } = useRoutes();
  const { history, location } = useRouter();
  const sideNav = useUsersSideNav();
  const previewDialog = useDialogState();
  const { enqueueSnackbarError, enqueueSnackbarSuccess } = useSnackbar();

  const [existingDoc, existingLoading] = useTargetAudienceById(
    audienceId ?? '__new__'
  );

  // Never applies when editing an existing saved audience.
  const prefill = !audienceId ? parsePrefill(location.search) : undefined;

  const [name, setName] = useState(prefill?.name ?? '');
  const [rule, setRule] = useState<Rule<UserField>>(
    prefill?.rule ?? { ...EMPTY_RULE_GROUP }
  );
  const initialized = useRef(false);

  useEffect(() => {
    if (!audienceId) {
      initialized.current = true;
      return;
    }
    if (!existingDoc || initialized.current) return;
    initialized.current = true;
    setName(existingDoc.data.name);
    setRule(existingDoc.data.rule);
  }, [audienceId, existingDoc]);

  const config = useUserRuleConfigWithAutocompletion(
    org.id,
    org.customUserFields,
    org.tz
  );

  const canSave = name.trim().length > 0;

  const handleSave = async () => {
    // The backend's targetAudience service only persists base operators with
    // scalar values. RuleBuilder pre-expands its set operators once a value is
    // entered, but a freshly-picked field with nothing chosen yet is still in
    // its un-expanded shape; catching that here avoids a failed round-trip.
    if (hasIncompleteCondition(rule)) {
      enqueueSnackbarError(
        t(
          "Finish or remove the filter row that's missing a value before saving."
        )
      );
      return;
    }
    // A blank-operator row can still carry an array (e.g. a rule prefilled
    // from the task builder); store the scalar shape the backend accepts.
    const ruleToSave = normalizeBlankOperatorValues(rule);
    try {
      if (audienceId) {
        await updateTargetAudience({ id: audienceId, name: name.trim(), rule: ruleToSave });
      } else {
        await createTargetAudience({ orgId: org.id, name: name.trim(), rule: ruleToSave });
      }
      enqueueSnackbarSuccess(t('Audience saved.'));
      history.push(ROUTES.users.targetAudiences.list.url());
    } catch (err: any) {
      enqueueSnackbarError(
        err?.message || t('Failed to save audience. Please try again.')
      );
    }
  };

  const handleCancel = () => {
    history.push(ROUTES.users.targetAudiences.list.url());
  };

  if (audienceId === 'new') {
    // This app's routes aren't wrapped in a <Switch> (see App.tsx's
    // RoutesPushable — a plain div), so /users/target-audiences/new matches
    // BOTH the literal `new` route AND this edit route's `:audienceId`
    // pattern (with audienceId literally the string "new"). Both mount
    // simultaneously. Render nothing here rather than redirecting away —
    // an aggressive redirect would hijack navigation from the sibling `new`
    // route's own component, which is the one actually meant to render.
    // Mirrors how PageExperienceDetails tolerates the identical
    // /experiences/create vs. /experiences/:expId collision.
    return null;
  }
  if (audienceId && existingLoading) return <Loader height={400} />;
  if (audienceId && !existingDoc) {
    return <Redirect to={ROUTES.users.targetAudiences.list.url()} />;
  }
  if (audienceId && existingDoc && existingDoc.data.orgId !== org.id) {
    return <Redirect to={ROUTES.users.targetAudiences.list.url()} />;
  }

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar
        sticky
        backButton={
          <Button
            startIcon={<ChevronLeft size={16} />}
            variant="text"
            color="primary"
            onClick={() => history.push(ROUTES.users.targetAudiences.list.url())}
          >
            {t('Back to target audiences')}
          </Button>
        }
      >
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {audienceId ? t('Edit audience') : t('New audience')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions />
      </PageToolbar>

      <FlexContainer direction="column" alignItems="stretch" spacing={2.5} fullWidth>
        <Card elevation={0} style={{ padding: 24 }}>
          <div style={{ maxWidth: 500, marginBottom: 24 }}>
            <TextField
              label={t('Audience name')}
              placeholder={t('e.g. Remote new hires')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
          </div>

          <CustomInputLabel fullWidth label={t('Enrollment filters')}>
            <RuleBuilder
              value={rule}
              onChange={(next) => next && setRule(next)}
              config={config}
              nullable
            />
          </CustomInputLabel>

          <FlexContainer justifyContent="flex-end" style={{ marginTop: 16 }}>
            <Button
              onClick={previewDialog.openDialog}
              startIcon={<Eye size={16} />}
              color="primary"
            >
              {t('Preview currently matching employees')}
            </Button>
          </FlexContainer>

          {previewDialog.dialogOpen && (
            <AudienceMatchDialog
              orgId={org.id}
              open={true}
              onClose={previewDialog.closeDialog}
              rule={rule}
            />
          )}
        </Card>

        <FlexContainer justifyContent="flex-end">
          <Button variant="outlined" color="default" onClick={handleCancel}>
            {t('Cancel')}
          </Button>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            disabled={!canSave}
            pending={t('Saving...')}
            onClick={handleSave}
          >
            {t('Save audience')}
          </ButtonWithPromise>
        </FlexContainer>
      </FlexContainer>
    </PageBody>
  );
};

export const PageTargetAudienceBuilder = ({
  audienceId
}: {
  audienceId: string | null;
}) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  const route = audienceId
    ? ROUTES.users.targetAudiences.edit
    : ROUTES.users.targetAudiences.new;

  if (!hasPermissionToAccessPage(currentUser, route)) {
    return <NoAccess />;
  }

  return <TargetAudienceBuilderContent audienceId={audienceId} />;
};
